"use client";

import React from "react";
import { DetailDrawer, DetailDrawerSkeleton } from "@/components/ui/detail-drawer";
import { Text } from "@/components/ui/text";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SectionsService } from "@/api-client/sections";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { ExternalLink } from "lucide-react";

interface AlignDetailDrawerProps {
  alignmentId: string | number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AlignDetailDrawer: React.FC<AlignDetailDrawerProps> = ({
  alignmentId,
  open,
  onOpenChange,
}) => {
  const { data, isLoading, error } = useAuthenticatedQuery<{
    id: number;
    title: string;
    description: string;
    explanation: string;
    capabilities: string | null;
    reasoning?: string;
    sources: Array<{
      name: string;
      url: string;
      type: string;
    }> | null;
  }>({
    queryKey: ["alignmentDetails", alignmentId],
    queryFn: () => {
      if (!alignmentId) throw new Error("Alignment ID is required");
      return SectionsService.getAlignmentDetails(alignmentId);
    },
    enabled: open && alignmentId !== null,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract name from URL if name is missing
  const getSourceName = (source: { name: string; url: string; type: string }) => {
    if (source.name && source.name.trim()) {
      return source.name;
    }
    if (source.url) {
      try {
        const url = new URL(source.url);
        // Get domain name (e.g., "example.com" from "https://www.example.com/path")
        let domain = url.hostname.replace(/^www\./, '');
        // Capitalize first letter and remove common TLDs for cleaner display
        return domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
      } catch {
        // If URL parsing fails, return a cleaned version of the URL
        return source.url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      }
    }
    return 'Source';
  };

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Aligns Details"
      side="right"
    >
      {isLoading && <DetailDrawerSkeleton />}
      
      {error ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Text size="base" weight="medium" color="muted">
            Failed to load alignment details. Please try again.
          </Text>
        </div>
      ) : null}

      {!isLoading && data ? (
        <>
          {/* Title Section */}
          <div className="space-y-2 pb-4">
            <Text as="h3" size="xl" weight="semibold" color="default" className="leading-tight">
              {data.title}
            </Text>
          </div>

          <Separator className="my-4" />

          {/* Description Section */}
          <div className="space-y-3 pb-4">
            <Text as="h4" size="base" weight="semibold" color="default">
            Insight
            </Text>
            <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
              {data.description}
            </Text>
          </div>

          {/* Explanation Section */}
          {data.explanation && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3 pb-4">
                <Text as="h4" size="base" weight="semibold" color="default">
                Context
                </Text>
                <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                  {data.explanation}
                </Text>
              </div>
            </>
          )}

          {/* Capabilities Section */}
          {data.capabilities && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3 pb-4">
                <Text as="h4" size="base" weight="semibold" color="default">
                  Capabilities
                </Text>
                <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                  {data.capabilities}
                </Text>
              </div>
            </>
          )}

          {/* Rationale Section */}
          {data.reasoning && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3 pb-4">
                <Text as="h4" size="base" weight="semibold" color="default">
                  Rationale
                </Text>
                <Text size="base" weight="normal" color="default" className="leading-relaxed text-muted-foreground">
                  {data.reasoning}
                </Text>
              </div>
            </>
          )}

          {/* Sources Section */}
          {data.sources && data.sources.length > 0 && (
            <>
              <Separator className="my-4" />
              <div className="space-y-3">
                <Text as="h4" size="base" weight="semibold" color="default">
                  Sources
                </Text>
                <div className="flex flex-wrap gap-2.5">
                  {data.sources.map((source, index) => {
                    const displayName = getSourceName(source);
                    return source.url ? (
                      <a
                        key={index}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center group"
                      >
                        <Badge 
                          variant="secondary" 
                          className="bg-secondary cursor-pointer hover:bg-secondary/80 transition-all duration-200 hover:shadow-md hover:scale-105 group-hover:gap-1.5 gap-1"
                        >
                          <Text size="xs" weight="medium" color="default">
                            {displayName}
                          </Text>
                          <ExternalLink className="w-3 h-3 ml-1.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                        </Badge>
                      </a>
                    ) : (
                      <Badge key={index} variant="secondary" className="bg-secondary">
                        <Text size="xs" weight="medium" color="default">
                          {displayName}
                        </Text>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </>
      ) : null}
    </DetailDrawer>
  );
};

